import { useState, useRef, useEffect } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Wand2, MessageSquare, X, Mic, Star, Save, Calculator, FileText, Eye, AlertTriangle } from "lucide-react";
import { PROMPT_TEMPLATES } from "@/data/promptTemplates";

const RAND = Math.random().toString(36).slice(2);
const MODES = [["layman", "Plain"], ["balanced", "Balanced"], ["developer", "Developer"]];

// #29 BeCo clarity gauge — clarity-versus-complexity heuristic (client-side, no credits).
function clarity(p) {
  const t = (p || "").trim();
  if (!t) return { score: 0, label: "Empty" };
  const w = t.split(/\s+/).length;
  let s = Math.min(40, w * 2);
  if (/\b(for|users?|customers?|clients?|team|members?)\b/i.test(t)) s += 15;
  if (/\b(sell|book|manage|track|share|schedule|pay|store|report|list)\b/i.test(t)) s += 15;
  if (/\b(uk|eu|kenya|us|global|region|country|market)\b/i.test(t)) s += 12;
  if (/\b(with|including|such as|feature|able to|include)\b/i.test(t)) s += 10;
  if (w > 80) s -= 10;
  s = Math.max(0, Math.min(100, s));
  return { score: s, label: s < 35 ? "Vague" : s < 70 ? "Getting clearer" : "Clear" };
}

// #32 conflict detection — contradictory requirement pairs.
const CONFLICTS = [
  { a: [/no (login|accounts?)/, /anonymous/, /without (login|accounts?)/], b: [/\blogin\b/, /sign ?in/, /user accounts?/, /\bmembers?\b/, /profiles?/], msg: "You mention both 'no accounts' and user logins — decide whether users sign in." },
  { a: [/\bfree\b/, /no (payment|fees|charge)/], b: [/subscription/, /\bpaid\b/, /billing/, /\bpayments?\b/, /\bcharge\b/], msg: "You mention both 'free' and paid/subscription — clarify the pricing model." },
  { a: [/store nothing/, /don'?t store/, /no data (stored|kept)/], b: [/\bsave\b/, /\bstore\b/, /database/, /records?/, /history/], msg: "You mention storing nothing but also saving records — clarify what is persisted." },
  { a: [/\boffline\b/], b: [/real-?time/, /live sync/, /cloud sync/], msg: "You mention offline and real-time sync — clarify how data syncs." },
];
function detectConflicts(p) {
  const t = (p || "").toLowerCase();
  return CONFLICTS.filter(c => c.a.some(r => r.test(t)) && c.b.some(r => r.test(t))).map(c => c.msg);
}

export default function PromptCoach({ prompt, onAppend, onSpecConfirmed, projectId }) {
  const sid = projectId ? `proj-${projectId}` : RAND;
  const [tips, setTips] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [vern, setVern] = useState(null);
  const [clar, setClar] = useState(null);
  const [answered, setAnswered] = useState([]);      // #26 clarify memory
  const [mode, setMode] = useState("balanced");        // #30 explanation register
  const [preview, setPreview] = useState(null);        // #31 inline diff preview
  const [cost, setCost] = useState(null);              // #35 cost preview
  const [favs, setFavs] = useState([]);                // #33 favourites
  const [listening, setListening] = useState(false);   // #27 voice
  const [tmpl, setTmpl] = useState("");
  const timer = useRef(null);
  const recRef = useRef(null);

  const g = clarity(prompt);
  const conflicts = detectConflicts(prompt);

  // #25 persist coach state per project
  useEffect(() => {
    try {
      const raw = localStorage.getItem(`kd_coach_${sid}`);
      if (raw) { const d = JSON.parse(raw); setSuggestions(d.suggestions || []); setClar(d.clar || null); setAnswered(d.answered || []); }
    } catch { /* ignore */ }
    try { setFavs(JSON.parse(localStorage.getItem("kd_fav_prompts") || "[]")); } catch { /* ignore */ }
  }, [sid]);
  useEffect(() => {
    try { localStorage.setItem(`kd_coach_${sid}`, JSON.stringify({ suggestions, clar, answered })); } catch { /* ignore */ }
  }, [suggestions, clar, answered, sid]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!prompt || prompt.trim().length < 4) { setTips(null); return; }
    timer.current = setTimeout(async () => {
      try { const { data } = await api.post("/coach/prompt-tips", { prompt, session_id: sid }); setTips(data); } catch { /* best-effort */ }
    }, 1200);
    return () => timer.current && clearTimeout(timer.current);
  }, [prompt, sid]);

  const dismiss = async (key) => {
    try { await api.post("/coach/prompt-tips/dismiss", { key }); } catch { /* ignore */ }
    setTips(t => t ? { ...t, missing_feature_alerts: t.missing_feature_alerts.filter(a => a !== key),
      enhancement_chips: t.enhancement_chips.filter(c => c.label !== key) } : t);
  };
  const runSuggest = async () => {
    setSuggesting(true);
    try { const { data } = await api.post("/coach/suggest", { prompt, session_id: sid, mode }); setSuggestions(data.suggestions || []); }
    catch (e) { toast.error(errMsg(e)); } finally { setSuggesting(false); }
  };
  const runVernacular = async () => {
    try { const { data } = await api.post("/coach/vernacular", { prompt, session_id: sid }); setVern(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const runClarify = async () => {
    try { const { data } = await api.post("/coach/clarify", { prompt }); setClar(data.questions); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const runCost = async () => {
    const w = (prompt || "").split(/\s+/).filter(Boolean).length;
    const complexity = Math.min(10, Math.max(1, Math.round(w / 15)));
    const feature_count = Math.min(12, ((prompt || "").match(/,|\band\b|\bwith\b|include/gi) || []).length + 2);
    try { const { data } = await api.post("/cost-preview", { complexity, feature_count }); setCost(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const answerClarify = (q, o) => {
    onAppend?.(` (${q} ${o})`);
    setAnswered(a => [...a.filter(x => x.q !== q), { q, o }]);
  };
  const saveFav = () => {
    if (!prompt?.trim()) return;
    const next = [{ id: Date.now(), text: prompt.trim() }, ...favs].slice(0, 20);
    setFavs(next); localStorage.setItem("kd_fav_prompts", JSON.stringify(next)); toast.success("Saved to favourites");
  };
  const delFav = (id) => {
    const next = favs.filter(f => f.id !== id);
    setFavs(next); localStorage.setItem("kd_fav_prompts", JSON.stringify(next));
  };
  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice input isn't supported in this browser."); return; }
    if (listening) { recRef.current?.stop(); return; }
    const r = new SR(); r.lang = "en-GB"; r.interimResults = false; r.continuous = false;
    r.onresult = (e) => { const t = Array.from(e.results).map(x => x[0].transcript).join(" "); onAppend?.(" " + t); };
    r.onend = () => setListening(false); r.onerror = () => setListening(false);
    recRef.current = r; setListening(true); r.start();
  };
  const useTemplate = () => {
    const t = PROMPT_TEMPLATES.find(x => x.id === tmpl);
    if (!t) return;
    if (prompt?.trim()) onAppend?.(" " + t.text); else onSpecConfirmed?.(t.text);
    toast.success(`Inserted "${t.name}" template`);
  };

  return (
    <div style={{ marginTop: 12 }} data-testid="prompt-coach">
      {/* #29 clarity gauge + #35 cost preview */}
      <div className="k-control" data-testid="beco-gauge" style={{ marginBottom: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
          <span className="k-muted">BeCo clarity (clarity vs complexity guide)</span>
          <span className="k-heading" data-testid="beco-label" style={{ fontWeight: 600 }}>{g.label} · {g.score}/100</span>
        </div>
        <div style={{ height: 6, borderRadius: 999, background: "var(--control)", marginTop: 6, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${g.score}%`, background: "var(--accent-primary)", transition: "width .3s cubic-bezier(0,0,.2,1)" }} />
        </div>
        {cost && <p className="k-muted" data-testid="cost-preview" style={{ fontSize: 12, marginTop: 8 }}>
          Estimated build: ~{cost.estimated_quote_credits} credits (projected {cost.projected_credits}, cap {cost.hard_cap_credits}). {cost.note}</p>}
      </div>

      {/* #32 conflict warnings */}
      {conflicts.map((c, i) => (
        <div key={i} className="k-control" data-testid="conflict-warning" style={{ marginBottom: 6, display: "flex", gap: 8, alignItems: "flex-start" }}>
          <AlertTriangle size={15} style={{ marginTop: 2 }} /><span style={{ fontSize: 13 }}>{c}</span>
        </div>
      ))}

      {/* action row */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8, alignItems: "center" }}>
        {/* #30 explanation register */}
        <div style={{ display: "flex", gap: 3, padding: 3, borderRadius: 8, border: "1px solid var(--border)" }} data-testid="coach-mode-toggle">
          {MODES.map(([m, l]) => (
            <button key={m} data-testid={`coach-mode-${m}`} onClick={() => setMode(m)}
              style={{ padding: "4px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", border: "none",
                background: mode === m ? "var(--accent-primary)" : "transparent", color: mode === m ? "#111113" : "var(--muted)" }}>{l}</button>
          ))}
        </div>
        <button className="k-btn k-btn-ghost" data-testid="coach-suggest-btn" onClick={runSuggest} disabled={suggesting || !prompt}>
          <Sparkles size={14} /> {suggesting ? "Suggesting…" : "Suggest"}
        </button>
        <button className="k-btn k-btn-ghost" data-testid="coach-vernacular-btn" onClick={runVernacular} disabled={!prompt}><Wand2 size={14} /> Normalise slang</button>
        <button className="k-btn k-btn-ghost" data-testid="coach-clarify-btn" onClick={runClarify} disabled={!prompt}><MessageSquare size={14} /> Clarify</button>
        <button className="k-btn k-btn-ghost" data-testid="coach-cost-btn" onClick={runCost} disabled={!prompt}><Calculator size={14} /> Cost preview</button>
        <button className="k-btn k-btn-ghost" data-testid="coach-voice-btn" onClick={toggleVoice}
          style={listening ? { borderColor: "var(--accent-secondary)", color: "var(--accent-secondary)" } : undefined}>
          <Mic size={14} /> {listening ? "Listening…" : "Voice"}
        </button>
        <button className="k-btn k-btn-ghost" data-testid="coach-fav-save" onClick={saveFav} disabled={!prompt}><Save size={14} /> Save</button>
      </div>

      {/* #28 templates + #33 favourites */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10, alignItems: "center" }}>
        <FileText size={14} className="k-muted" />
        <select className="k-control" data-testid="template-select" value={tmpl} onChange={e => setTmpl(e.target.value)}
          style={{ width: "auto", fontSize: 13 }}>
          <option value="">Starter templates…</option>
          {PROMPT_TEMPLATES.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <button className="k-btn k-btn-ghost" data-testid="template-use" onClick={useTemplate} disabled={!tmpl}>Use</button>
        {favs.length > 0 && <span className="k-muted" style={{ fontSize: 12 }}>· Favourites:</span>}
        {favs.map(f => (
          <span key={f.id} className="k-badge" data-testid="fav-prompt" style={{ cursor: "pointer" }} title={f.text}>
            <Star size={11} /> <span onClick={() => onSpecConfirmed?.(f.text)}>{f.text.slice(0, 22)}…</span>
            <X size={11} style={{ marginLeft: 4 }} data-testid="fav-delete" onClick={() => delFav(f.id)} />
          </span>
        ))}
      </div>

      {/* #31 inline diff preview */}
      {preview && (
        <div className="k-control" data-testid="chip-preview" style={{ marginBottom: 8 }}>
          <div className="k-muted" style={{ fontSize: 12 }}><Eye size={12} /> This will append to your prompt:</div>
          <p className="k-heading" style={{ fontSize: 13, margin: "6px 0" }}>{preview.snippet}</p>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="k-btn k-btn-primary" data-testid="chip-preview-apply" style={{ padding: "6px 14px", fontSize: 13 }}
              onClick={() => { onAppend?.(preview.snippet); setPreview(null); }}>Apply</button>
            <button className="k-btn k-btn-ghost" style={{ padding: "6px 14px", fontSize: 13 }} onClick={() => setPreview(null)}>Dismiss</button>
          </div>
        </div>
      )}

      {tips?.missing_feature_alerts?.map((a, i) => (
        <div key={i} className="k-control" data-testid="missing-feature-alert" style={{ marginBottom: 6, display: "flex", justifyContent: "space-between", gap: 8 }}>
          <span className="k-tag-warn" style={{ fontSize: 13 }}>{a}</span>
          <X size={14} style={{ cursor: "pointer" }} data-testid="dismiss-tip" onClick={() => dismiss(a)} />
        </div>
      ))}
      {tips?.enhancement_chips?.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
          {tips.enhancement_chips.map((c, i) => (
            <button key={i} className="k-badge" style={{ cursor: "pointer" }} data-testid="enhancement-chip"
              onClick={() => setPreview({ label: c.label, snippet: c.snippet })}>{c.label}</button>
          ))}
        </div>
      )}
      {tips && <p className="k-muted" style={{ fontSize: 11 }}>{tips.note} {tips.capped ? "(tip limit reached this session)" : `(${tips.session_used}/${tips.session_cap})`}</p>}

      {suggestions.length > 0 && (
        <div className="k-control" data-testid="suggestions" style={{ marginBottom: 6 }}>
          <div className="k-muted" style={{ fontSize: 12, marginBottom: 4 }}>AI suggestions ({mode}, free — review before applying):</div>
          <div className="k-callout-accent" data-testid="coach-top-suggestion" style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--accent-secondary)", marginBottom: 4 }}>Recommended</div>
            <button className="k-link" style={{ cursor: "pointer", fontSize: 13, textAlign: "left" }}
              data-testid="suggestion-chip" onClick={() => onAppend?.(" " + suggestions[0])}>+ {suggestions[0]}</button>
          </div>
          {suggestions.slice(1).map((s, i) => (
            <button key={i} className="k-badge" style={{ cursor: "pointer", margin: 3 }} data-testid="suggestion-chip"
              onClick={() => onAppend?.(" " + s)}>+ {s}</button>
          ))}
        </div>
      )}

      {vern && (
        <div className="k-control" data-testid="vernacular-result" style={{ marginBottom: 6 }}>
          <div className="k-heading" style={{ fontSize: 13, fontWeight: 600 }}>I understood this as:</div>
          <p className="k-muted" style={{ fontSize: 13, margin: "4px 0" }}>{vern.understood_as}</p>
          {vern.standard_spec && <p className="k-muted" style={{ fontSize: 12 }}>{vern.standard_spec}</p>}
          <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
            <button className="k-btn k-btn-primary" data-testid="vernacular-confirm" style={{ padding: "6px 14px", fontSize: 13 }}
              onClick={() => { onSpecConfirmed?.(vern.standard_spec || vern.understood_as); setVern(null); toast.success("Confirmed"); }}>
              Yes, that's right
            </button>
            <button className="k-btn k-btn-ghost" data-testid="vernacular-reject" style={{ padding: "6px 14px", fontSize: 13 }}
              onClick={() => setVern(null)}>No, let me reword</button>
          </div>
          <p className="k-muted" style={{ fontSize: 11, marginTop: 6 }}>{vern.note}</p>
        </div>
      )}

      {answered.length > 0 && (
        <div className="k-control" data-testid="clarify-memory" style={{ marginBottom: 6 }}>
          <div className="k-muted" style={{ fontSize: 12, marginBottom: 4 }}>Answered so far:</div>
          {answered.map((a, i) => <div key={i} className="k-muted" style={{ fontSize: 12 }}>• {a.q} → <span className="k-heading">{a.o}</span></div>)}
        </div>
      )}

      {clar && (
        <div className="k-control" data-testid="clarify-result" style={{ marginBottom: 6 }}>
          {clar.map((q, i) => (
            <div key={i} style={{ marginBottom: 8 }}>
              <div className="k-heading" style={{ fontSize: 13, fontWeight: 600 }}>{q.q}</div>
              <div className="k-muted" style={{ fontSize: 11, marginBottom: 4 }}>Why: {q.why}</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {q.options.map((o, j) => (
                  <button key={j} className={`k-badge ${answered.some(a => a.q === q.q && a.o === o) ? "k-badge-accent" : ""}`}
                    style={{ cursor: "pointer" }} data-testid="clarify-option" onClick={() => answerClarify(q.q, o)}>{o}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
